import { Texture } from '../../../Constants';
import { whichStacksHaveThisAction } from '../GamePlayerInfo';
import GamePlayerPackageSender from './GamePlayerPackageSender';


export default class GamePlayerAPI {

    constructor(gamePlayerController) {
        this.gamePlayerController = gamePlayerController;
        this.packageSender = new GamePlayerPackageSender(this);

        this.gamePlayerData = this.gamePlayerController.gamePlayerData;
        this.patchGamePlayer = this.gamePlayerController.patchGamePlayer;
    }

    clickNext = () => {
        // PackageSender drops Click packages from statuses the backend
        // won't accept (see GamePlayerPackageSender for the allow-list).
        let typeName = 'Click';
        let packageValue = {
            type: 'Next',
            data: {
                type: 'Next'
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    clickAdvance = () => {
        // Sent by the FE-AI from ACTION_COMPLETE_ID to ask the
        // engine to advance to SELECT_ACTION_ID. Distinct from
        // clickNext (human Skip / BigButton2) so the backend can
        // tell the AI's "advance" intent apart from the human
        // "end my turn early" intent — they used to share the
        // same wire shape and races between the two backend
        // broadcasts (ACTION_COMPLETE then SELECT_ACTION) ended
        // Diversify turns prematurely. See
        // hsh-documents/CC Tasks/Complete/CC_FE_AI_ADVANCE_DISCRIMINATOR.md
        // for the saga.
        let typeName = 'Click';
        let packageValue = {
            type: 'Next',
            data: {
                type: 'Advance'
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    clickEndTurn = () => {
        // Manual §7.1 yield-mid-turn affordance. Same outer 'Next'
        // routing the legacy Done-with-Turn click uses (so
        // turn_click_handler still dispatches to next_button) but the
        // inner data.type is 'EndTurn' so next_button skips the state-
        // dependent dispatch and fires end_turn() directly.
        let typeName = 'Click';
        let packageValue = {
            type: 'Next',
            data: {
                type: 'EndTurn'
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    toggleActivation = (roomIndex) => {
        let packageValue = {
            type: 'Robot',
            data: {
                type: 'Station',
                station_grid_index: roomIndex
            }
        };
        this.packageSender.sendPackage('Robot', packageValue);
    }

    pushActionHexPosition = (actionHexPosition) => {
        // Single wire-level sender for human-driven Rotate. All callers
        // route through GamePlayerController._scheduleRotateBroadcast so
        // bursts collapse into one outgoing message.
        if (this.gamePlayerData.action_hex_locked) return;
        let typeName = 'ActionHex';
        let packageValue = {
            type: 'Rotate',
            data: {
                action_hex_position: actionHexPosition
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    aiRotateAndLockActionHex = (rotationIndex) => {
        this.actionHexPosition = rotationIndex;
        let typeName = 'ActionHex';
        let packageValue = {
            type: 'Rotate',
            data: {
                action_hex_position: rotationIndex,
                lock: true
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    lockAndClickActionGrid = (texture) => {
        // so the action grid isn't compiled, so it gets the wrong texture.
        // switch to "just send the right texture" and that worked.
        // don't really dig this design pattern.
        // NOTE: It would take a "stack" variable instead. Would be cleaner.
        // would require not starting with a faction action.

        const gamePlayerData = this.gamePlayerController.gamePlayerData;

        const actionId = Texture.ACTIONS.indexOf(texture);
        let stack = null;
        if (actionId > -1) {
            const stackData = whichStacksHaveThisAction(
                gamePlayerData, actionId);
            if (actionId < 7) {
                stack = 0;
                if (!stackData.stacks[0]) {
                    stack = 1;
                }
            }
        }

        let typeName = 'Click';
        let packageValue = {
            type: 'Lock&Skip',
            data: {
                type: 'Lock&Skip',
                contains_action: true,
                texture: texture,
                stack: stack,
                action_hex_position: gamePlayerData.action_hex_position,
                note: 'this is lockAndClickActionGrid',
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    takeFactionAction = (actionId) => {
        // Manual ruling per CC_RANDOM_FACTION_NO_HEX_LOCK: Faction
        // Actions do NOT lock the Action Hex. Send the Faction Action
        // package directly; do NOT route through lockAndClickActionGrid
        // (which sends Lock&Skip and locks the hex on both sides).
        let typeName = 'Click';
        let packageValue = {
            type: 'Faction Action',
            data: {
                type: 'Faction Action',
                action: actionId,
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    takeRandomAction = () => {
        // Manual ruling per CC_RANDOM_FACTION_NO_HEX_LOCK: Random
        // Actions do NOT lock the Action Hex. Backend picks the
        // station from available_random_actions[0]; the wire click
        // does not need to specify one.
        let typeName = 'Click';
        let packageValue = {
            type: 'Random Action',
            data: {
                type: 'Random Action',
                texture: 83,
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    selectAction = (actionGridIndex, texture, stack) => {
        let typeName = 'Click';
        let clickMode = 'ActionGrid';
        let packageValue = {
            type: clickMode,
            data: {
                action_grid_index: actionGridIndex,
                texture: texture,
                stack: stack
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    clickActionGrid = (actionGridIndex, texture) => {
        let typeName = 'Click';
        let clickMode = 'ActionGrid';
        let packageValue = {
            type: clickMode,
            data: {
                action_grid_index: actionGridIndex,
                texture: texture,
                note: 'clickActionGrid',
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    selectActionStack = (actionGridIndex, texture, stack) => {
        let typeName = 'Click';
        let clickMode = 'ActionGrid';
        let packageValue = {
            type: clickMode,
            data: {
                action_grid_index: actionGridIndex,
                texture: texture,
                stack: stack,
                note: 'this is selectActionStack'
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    addLodging = (factionId) => {
        let typeName = 'Click';
        let clickMode = 'ActionGrid';
        let packageValue = {
            type: clickMode,
            data: {
                faction: factionId,
                note: 'this is addLodging.'
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    pickPlayerOne = (actionGridIndex) => {
        let seatIndex = actionGridIndex - 1;
        let typeName = 'Click';
        let packageValue = {
            type: 'Choice',
            data: {
                type: 'Pick Player One',
                seat: seatIndex
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    initialLodgingChoice = (factionId) => {
        let typeName = 'Click';
        let packageValue = {
            type: 'InitialLodging',
            data: {
                type: 'InitialLodging',
                faction_id: factionId,
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    commitFactionChoice = (factionId) => {
        // Manual §3 in-game faction ceremony. factionId === null means
        // "auto-confirm my pre-assigned faction"; the engine reads
        // game_player.faction_id and treats it as the commit value.
        let typeName = 'Click';
        let packageValue = {
            type: 'ChooseFaction',
            data: {
                faction_id: factionId,
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    placeSpecialGuest = (dockIndex) => {
        let typeName = 'Click';
        let packageValue = {
            type: 'Special',
            data: {
                type: 'special',
                dock: dockIndex
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    placeGhost = (dockIndex) => {
        let typeName = 'Click';
        let packageValue = {
            type: 'Choice',
            data: {
                type: 'Ghost',
                dock: dockIndex
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    draw = (orbitIndex) => {
        let typeName = 'Click';
        let packageValue = {
            type: 'Advertisement',
            data: {
                type: 'draw',
                orbit: orbitIndex
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    placeGuest = (dockIndex, orbitIndex) => {
        if (![0, 1, 2, 3].includes(dockIndex)) {
            console.log('error')
        }


        let typeName = 'Click';
        let packageValue = {
            type: 'Advertisement',
            data: {
                type: 'place',
                orbit: orbitIndex,
                dock: dockIndex
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    clickSpecialTarget = (competitorId, orbitIndex) => {
        let typeName = 'Click';
        let packageValue = {
            type: 'Advertisement',
            data: {
                type: 'special',
                target: competitorId,
                orbit: orbitIndex,
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    clickAntiJuice = (soldRobots) => {
        let typeName = 'Click';
        let packageValue = {
            type: 'Anti-Juice',
            data: {
                type: 'sell',
                sold_robots: soldRobots,
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    clickDiscard = (orbitIndex) => {
        let typeName = 'Click';
        let packageValue = {
            type: 'Advertisement',
            data: {
                type: 'discard',
                orbit: orbitIndex
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    clickDraftChoice = (guestCardId) => {
        let typeName = 'Click';
        let packageValue = {
            type: 'Choice',
            data: {
                type: 'Draft',
                card: guestCardId
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    ghostFullAction = (
        soldRobots,
        target,
        dock=null
    ) => {
        let typeName = 'Click';
        let packageValue = {
            type: 'Ghost',
            data: {
                sold_robots: soldRobots,
                player_target: target,
            }
        };
        if (dock !== null) packageValue.data.dock = dock;
        this.packageSender.sendPackage(typeName, packageValue);
    }

    ghostCommitTarget = (target) => {
        // Manual §7.2.6: commit target BEFORE the Fate Die. Engine's
        // commit_target persists current_action_target then runs the
        // roll inline.
        let typeName = 'Click';
        let packageValue = {
            type: 'Ghost',
            data: {
                player_target: target,
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    ghostJuice = (soldRobots) => {
        let typeName = 'Click';
        let packageValue = {
            type: 'Ghost',
            data: {
                sold_robots: soldRobots
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    clickDeGhost = (dockIndex) => {
        let typeName = 'Click';
        let packageValue = {
            type: 'Ghost',
            data: {
                type: 'DeGhost',
                dock: dockIndex
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }


    lodgingFullAction = (
        soldRobots,
        target,
        faction=null,
        lodgingGridIndex=null
    ) => {
        let typeName = 'Click';
        let packageValue = {
            type: 'Lodging',
            data: {
                sold_robots: soldRobots,
                player_target: target,
            }
        };
        if (faction) packageValue.data.faction = faction;
        if (lodgingGridIndex !== null) packageValue.data.lodging_grid_index = lodgingGridIndex;
        this.packageSender.sendPackage(typeName, packageValue);
    }

    lodgingCommitTarget = (target) => {
        // Manual §7.2.2: commit target BEFORE the Fate Die. Engine's
        // commit_target persists current_action_target then runs the
        // roll inline.
        let typeName = 'Click';
        let packageValue = {
            type: 'Lodging',
            data: {
                player_target: target,
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    lodgingJuice = (soldRobots) => {
        let typeName = 'Click';
        let packageValue = {
            type: 'Lodging',
            data: {
                sold_robots: soldRobots
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    lodgingUpgrade = (lodgingGridIndex) => {
        let typeName = 'Click';
        let packageValue = {
            type: 'Lodging',
            data: {
                type: 'upgrade',
                lodging_grid_index: lodgingGridIndex
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    installFromStorage = (stationGridIndex, selectedStorageGridIndex) => {
        let typeName = 'Click';
        let packageValue = {
            type: 'Renovation',
            data: {
                type: 'install',
                install: selectedStorageGridIndex,
                destination: stationGridIndex
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    uninstallToStorage = (selectedStationGridIndex) => {
        let typeName = 'Click';
        let packageValue = {
            type: 'Renovation',
            data: {
                type: 'uninstall',
                uninstall: selectedStationGridIndex,
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    renovationSell = (stationIndex) => {
        let typeName = 'Click';
        let packageValue = {
            type: 'Renovation',
            data: {
                type: 'sell',
                sell: stationIndex
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    renovationMove = (selectedStationGridIndex, stationIndex) => {
        let typeName = 'Click';
        let packageValue = {
            type: 'Renovation',
            data: {
                type: 'move',
                move: selectedStationGridIndex,
                destination: stationIndex
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    researchSell = (storageGridIndex) => {
        let typeName = 'Click';
        let packageValue = {
            type: 'Research',
            data: {
                type: 'sell',
                storage_index: storageGridIndex,
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    researchUpgrade = (storageGridIndex) => {
        let typeName = 'Click';
        let packageValue = {
            type: 'Research',
            data: {
                type: 'upgrade',
                storage_grid_index: storageGridIndex,
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    researchBuy = (actionGridIndex) => {
        let typeName = 'Click';
        let packageValue = {
            type: 'ActionGrid',
            data: {
                type: 'buy',
                action_grid_index: actionGridIndex
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    researchBuyByRoomId = (roomId) => {
        let typeName = 'Click';
        let packageValue = {
            type: 'Research',
            data: {
                type: 'add',
                room: roomId
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    clickScoreDock = (dockId) => {
        let typeName = 'Click';
        let packageValue = {
            type: 'Score',
            data: {
                type: 'dock',
                dock: dockId
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }

    clickVanguardLodging = (lodgingGridIndex) => {
        let typeName = 'Click';
        let packageValue = {
            type: 'Lodging',
            data: {
                type: 'vanguard result',
                position: lodgingGridIndex
            }
        };
        this.packageSender.sendPackage(typeName, packageValue);
    }
}
